import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Organization } from '../../models';
import AuthLoading from '../shared/loading/AuthLoading';
import {
  isTrialPeriodExpired,
  isStripePastDueExpired,
  parseBillingPeriodEnd,
} from '../../utils/billingPeriodEnd';
import {
  canAccessPath,
  defaultLandingPath,
  permissionForPath,
} from '../../utils/panelPermissions';

const getRequestedOrganizationIdFromSearch = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const requested =
    params.get('organizationId') || params.get('organization_id') || params.get('org');
  const normalized = requested?.trim();
  return normalized ? normalized : null;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  authLoading: boolean;
  isAdmin: boolean;
  panelPermissions?: string[] | null;
  organization: Organization | null;
  userOrganizations: Organization[];
  showOrgSelector: boolean;
  isGodMode: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  authLoading,
  isAdmin,
  panelPermissions = null,
  organization,
  userOrganizations,
  showOrgSelector,
  isGodMode,
}) => {
  const location = useLocation();

  if (authLoading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    const requestedOrganizationId = getRequestedOrganizationIdFromSearch(
      location.search
    );
    return (
      <Navigate
        to="/auth"
        state={{ from: location, requestedOrganizationId }}
        replace
      />
    );
  }

  if (showOrgSelector && userOrganizations.length > 1 && !organization) {
    return (
      <Navigate to="/organization-select" state={{ from: location }} replace />
    );
  }

  const adminOnlyRoutes = [
    '/inventory',
    '/configuration',
    '/suppliers',
    '/purchases',
  ];

  const requiresAdmin = adminOnlyRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  const organizationRequiredRoutes = [
    '/inventory',
    '/orders',
    '/quotations',
    '/returns',
    '/client-users',
    '/configuration',
    '/suppliers',
    '/purchases',
    '/accounts-receivable',
    '/accounts-payable',
    '/clients',
    '/vendors',
  ];

  const requiresOrganization = organizationRequiredRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  if (requiresOrganization && !organization) {
    return (
      <Navigate to="/organization-select" state={{ from: location }} replace />
    );
  }

  const needsPanelPermissions =
    !!permissionForPath(location.pathname) ||
    location.pathname.startsWith('/configuration');

  // Wait for panel permissions before gating mapped admin routes.
  if (
    isAuthenticated &&
    isAdmin &&
    !isGodMode &&
    panelPermissions == null &&
    !authLoading &&
    needsPanelPermissions
  ) {
    return <AuthLoading />;
  }

  const hasPermissionAccess = canAccessPath(
    location.pathname,
    panelPermissions,
    isGodMode
  );
  if (!hasPermissionAccess) {
    return (
      <Navigate
        to={defaultLandingPath(panelPermissions, isGodMode)}
        replace
      />
    );
  }

  if (
    requiresAdmin &&
    !isAdmin &&
    !isGodMode &&
    (panelPermissions == null || panelPermissions.length === 0)
  ) {
    return <Navigate to="/home" replace />;
  }

  if (
    !isGodMode &&
    organization &&
    organization.active === false &&
    location.pathname !== '/organization-select'
  ) {
    return (
      <Navigate
        to="/organization-select"
        state={{ from: location, orgDeactivated: true }}
        replace
      />
    );
  }

  if (
    !isGodMode &&
    organization &&
    organization.billing?.subscriptionStatus === 'ves_rejected' &&
    location.pathname !== '/paywall'
  ) {
    return (
      <Navigate
        to="/paywall"
        state={{
          organizationId: organization.id,
          from: location,
          subscriptionStatus: 'ves_rejected',
        }}
        replace
      />
    );
  }

  const billing = organization?.billing;
  const status = billing?.subscriptionStatus;
  if (
    !isGodMode &&
    organization &&
    billing?.paymentMethod === 'ves' &&
    status === 'active' &&
    location.pathname !== '/paywall'
  ) {
    const endDate = parseBillingPeriodEnd(billing?.currentPeriodEnd);
    const now = new Date();
    if (endDate && now > endDate) {
      const daysSincePeriodEnd = Math.floor(
        (now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSincePeriodEnd > 10) {
        return (
          <Navigate
            to="/paywall"
            state={{
              organizationId: organization.id,
              from: location,
              vesPaymentOverdue: true,
            }}
            replace
          />
        );
      }
    }
  }

  if (
    !isGodMode &&
    organization &&
    status === 'trialing' &&
    location.pathname !== '/paywall'
  ) {
    if (isTrialPeriodExpired(billing)) {
      return (
        <Navigate
          to="/paywall"
          state={{
            organizationId: organization.id,
            from: location,
            subscriptionStatus: status,
            trialExpired: true,
          }}
          replace
        />
      );
    }
  }

  if (
    !isGodMode &&
    organization &&
    status === 'past_due' &&
    isStripePastDueExpired(billing) &&
    location.pathname !== '/paywall'
  ) {
    return (
      <Navigate
        to="/paywall"
        state={{
          organizationId: organization.id,
          from: location,
          subscriptionStatus: status,
        }}
        replace
      />
    );
  }

  const allowedStatuses = ['active', 'trialing', 'pending_ves'];
  const hasPaywallAccess =
    status &&
    (allowedStatuses.includes(status) ||
      (status === 'past_due' && !isStripePastDueExpired(billing)));

  if (
    !isGodMode &&
    organization &&
    !hasPaywallAccess &&
    location.pathname !== '/paywall'
  ) {
    return (
      <Navigate
        to="/paywall"
        state={{
          organizationId: organization.id,
          from: location,
          subscriptionStatus: organization.billing?.subscriptionStatus,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
